import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    createStyles,
    makeStyles,
    Theme,
    useTheme,
} from '@material-ui/core/styles'
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core'
import PhotoCamera from '@material-ui/icons/PhotoCamera'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteIcon from '@material-ui/icons/Delete'

import { useUser } from '../../../../hooks'
import { Link } from 'react-router-dom'
// import { uploadFile } from '../../../helper/s3upload'
import { SubmitHandler, useForm } from 'react-hook-form'
import LoopIcon from '@material-ui/icons/Loop'
import {
    axiosInstance,
    formatPhoneForDisplay,
    INVALID_PHONE_MESSAGE,
    isInvalidPhoneError,
    utcOffsetString,
} from '../../../../helpers'
import { UpdateUserRequest, UpdateUserRequestFormData } from '../../../../store'

import { toast } from 'react-toastify'
import axios from 'axios'
import { AddressForm, AddressState, Container } from '../../../../components'
import { Address } from '../../../../models'
import { typographyStyle } from '../../../../theme/typographyStyle'
import {
    useUploadApartmentLogo,
    useDeleteApartmentLogo,
} from '../../../../hooks/useMoveOutManagement/useMoveOutManagement'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        closeBtn: {
            background: '#FFF',
            color: '#E9190F',
            border: '1px solid #E9190F',
            fontSize: '0.875rem',
            textTransform: 'inherit',
            fontWeight: 500,
            borderRadius: '5px',
            marginLeft: '10px',
        },
        saveBtn: {
            background: '#008C85',
            color: '#FFF',
            border: '1px solid #008C85',
            fontSize: '0.875rem',
            textTransform: 'inherit',
            fontWeight: 500,
            borderRadius: '5px',
        },
        getQuoteForm: {
            padding: '10px 0',
            '& .MuiFormControl-root': {
                margin: '5px 0',
            },
            '& .MuiFormLabel-root': {
                fontSize: '0.75rem',
                color: '#000',
                fontWeight: 700,
            },
            '& .MuiInputBase-input': {
                fontSize: '0.813rem',
            },
        },
        uploadInput: {
            display: 'none',
        },
        fileUploadBtn: {
            position: 'absolute',
            right: '-5%',
            bottom: '-10%',
            color: 'rgb(255, 255, 255)',
            background: 'rgb(44, 128, 255)',
            padding: '4px',
            border: '5px solid #fafafa',
            fontSize: '0.5rem',
            '& .MuiSvgIcon-root': {
                height: '1.2em',
                width: '1.2em',
                '& path': {
                    stroke: '#000',
                },
                '& circle': {
                    stroke: '#FFF',
                    strokeWidth: '10px',
                },
            },
        },
        rotateIcon: {
            animation: 'spin 01s linear infinite',
        },
        sectionCard: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
            backgroundColor: '#fff',
        },
        sectionTitle: {
            fontWeight: 700,
            fontSize: '1.1rem',
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
        },
        sectionDivider: {
            borderBottom: `1px solid ${theme.palette.divider}`,
            marginBottom: theme.spacing(2.5),
            paddingBottom: theme.spacing(2.5),
        },
        logoPreviewBox: {
            width: 100,
            height: 100,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[50],
            overflow: 'hidden',
            flexShrink: 0,
        },
        primaryBtn: {
            background: '#008C85',
            color: '#FFF',
            fontSize: '0.875rem',
            textTransform: 'inherit' as const,
            fontWeight: 500,
            borderRadius: '5px',
            boxShadow: 'none',
            '&:hover': {
                background: '#00756F',
                boxShadow: 'none',
            },
        },
    }),
)

export const GeneralSetting = () => {
    const classes = useStyles()

    const theme = useTheme()

    const { workspaceUser, actions } = useUser()
    const { updateUser } = actions

    const [addressState, setAddressState] = useState<AddressState>({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
    })

    const [companyTzOffset, setCompanyTzOffset] = useState({
        utcOffset: 0,
        dstOffset: 0,
    })

    const [profilePic, setProfilePic] = useState<File | null>(null)

    const [profilePicString, setProfilePicString] = useState(
        workspaceUser?.profile_picture,
    )

    const apartmentId = workspaceUser?.active_workspace?.id ?? null
    const currentLogo = workspaceUser?.active_workspace?.profile_picture ?? null
    const [logoPreview, setLogoPreview] = useState<string | null>(currentLogo)
    const logoInputRef = useRef<HTMLInputElement>(null)

    const {
        mutate: uploadLogo,
        isLoading: isLogoUploading,
    } = useUploadApartmentLogo()
    const {
        mutate: deleteLogo,
        isLoading: isLogoDeleting,
    } = useDeleteApartmentLogo()

    useEffect(() => {
        setLogoPreview(currentLogo)
    }, [currentLogo])

    const handleLogoUpload = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file || !apartmentId) return
            const localPreview = URL.createObjectURL(file)
            setLogoPreview(localPreview)
            uploadLogo(
                { apartmentId, file },
                {
                    onSuccess: (data) => {
                        URL.revokeObjectURL(localPreview)
                        setLogoPreview(data.profile_picture)
                    },
                    onError: () => {
                        URL.revokeObjectURL(localPreview)
                        setLogoPreview(currentLogo)
                    },
                },
            )
        },
        [apartmentId, currentLogo, uploadLogo],
    )

    const handleLogoRemove = useCallback(() => {
        if (!apartmentId) return
        setLogoPreview(null)
        if (logoInputRef.current) {
            logoInputRef.current.value = ''
        }
        deleteLogo(
            { apartmentId },
            {
                onError: () => {
                    setLogoPreview(currentLogo)
                },
            },
        )
    }, [apartmentId, currentLogo, deleteLogo])

    const uploadProfile = (file: File | null) => {
        if (file) {
            const formData = new FormData()
            formData.append('name', file.name)
            axiosInstance
                .post('user/presigned_url/', formData)
                .then(async (res: any) => {
                    if (res && res.data) {
                        const signedUrl = res.data.url
                        console.log('signed url is....', signedUrl)
                        axios.put(signedUrl)
                        const formDataUpload = new FormData()
                        formData.append('name', file)
                        const result = await fetch(signedUrl, {
                            method: 'PUT',
                            body: formDataUpload,
                            headers: {
                                'Content-Type': 'image/jpeg',
                                'x-amz-acl': 'public-read',
                                'x-amz-meta-key': '12345',
                                'x-amz-meta-otherInfo': 'other_data',
                            },
                        })
                        console.log('result is....', result)
                    }
                    console.log('response is...', res)
                })
                .catch((err: any) => {
                    console.log('error is....', err)
                })
        }
    }

    let filePreview =
        'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQQ5srpTZoAXWINllDurGscx_Pqg_foehqAkQ&usqp=CAU'

    if (profilePic) {
        filePreview = URL.createObjectURL(profilePic)
    } else if (profilePicString) {
        filePreview = profilePicString
    }

    useEffect(() => {
        getFormDataValues()
        console.log('workspace user....', workspaceUser)

        if (workspaceUser?.active_workspace) {
            axiosInstance
                .get(`/company/${workspaceUser.active_workspace.id}/`)
                .then((res) => {
                    console.log('res is...', res)

                    if (res.data && res.data.primary_address) {
                        const pimaryAddress: Address | null =
                            res.data.primary_address
                        if (pimaryAddress) {
                            setAddressState({
                                addressLine1: pimaryAddress.address1,
                                addressLine2: pimaryAddress.address2,
                                city: pimaryAddress.city,
                                state: pimaryAddress.state,
                                zip: pimaryAddress.zip_code,
                                country: pimaryAddress.country,
                            })
                        }

                        setCompanyTzOffset({
                            utcOffset: res.data.primary_address.utc_offset,
                            dstOffset: res.data.primary_address.dst_offset,
                        })
                    }
                })
                .catch((e) => {
                    console.log('error is...', e)
                })
        }
        console.log(workspaceUser)
    }, [workspaceUser])

    const {
        register,
        handleSubmit,
        reset,
        setError,
        getValues,
        formState: { errors },
    } = useForm({
        mode: 'all',
    })
    console.log('erros are...', errors)
    const getFormDataValues = () => {
        reset({
            name: workspaceUser?.name || '',
            profile_color: workspaceUser?.profile_color || '',
            phone: formatPhoneForDisplay(workspaceUser?.phone),
            cell_phone: formatPhoneForDisplay(workspaceUser?.cell_phone),
            profile_picture: workspaceUser?.profile_picture || '',
            email: workspaceUser?.email || '',
        })
    }

    const [addressLoading, setAddressLoading] = useState(false)

    const [isLoading, setIsLoading] = useState(false)
    const [isUpdatingPortfolioReport, setIsUpdatingPortfolioReport] = useState(
        false,
    )

    const portfolioScheduleReportEnabled =
        workspaceUser?.receive_portfolio_schedule_report ?? false

    const handlePortfolioScheduleReportToggle = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (!workspaceUser) return

        const enabled = event.target.checked
        setIsUpdatingPortfolioReport(true)

        try {
            await updateUser({
                isCurrentUser: true,
                params: { id: workspaceUser.id },
                body: { receive_portfolio_schedule_report: enabled },
            })
            toast.success(
                enabled
                    ? 'Daily Portfolio Schedule Reports enabled'
                    : 'Daily Portfolio Schedule Reports disabled',
            )
        } catch {
            toast.error('Failed to update portfolio schedule report preference')
        } finally {
            setIsUpdatingPortfolioReport(false)
        }
    }

    const onSubmit = (data: any, event: any) => {
        console.log('submit data....', data)
        event.preventDefault()
        setIsLoading(true)

        const body = new FormData()

        if (data.name) {
            body.append('name', data.name)
        }

        if (data.email) {
            body.append('email', data.email)
        }

        if (data.phone) {
            body.append('phone', data.phone)
        }

        if (data.cell_phone) {
            body.append('cell_phone', data.cell_phone)
        }

        if (data.profile_color) {
            body.append('profile_color', data.profile_color)
        }

        if (profilePic) {
            body.append('profile_picture', profilePic)
        }

        const req: UpdateUserRequestFormData = {
            isCurrentUser: true,
            params: {
                id: workspaceUser?.id || 0,
            },
            body: body,
        }

        updateUser(req)
            .then((res: any) => {
                console.log('response data is...', res)
                toast.success(`Profile updated!`)
                setIsLoading(false)
            })
            .catch((e) => {
                setIsLoading(false)
                if (isInvalidPhoneError(e)) {
                    const values = getValues()
                    if ((values.phone ?? '').trim()) {
                        setError('phone', {
                            type: 'invalid',
                            message: INVALID_PHONE_MESSAGE,
                        })
                    }
                    if ((values.cell_phone ?? '').trim()) {
                        setError('cell_phone', {
                            type: 'invalid',
                            message: INVALID_PHONE_MESSAGE,
                        })
                    }
                }
            })
    }

    const utcOffsetSeconds = companyTzOffset.utcOffset

    const offsetStr = utcOffsetString(
        utcOffsetSeconds,
        companyTzOffset.dstOffset,
    )

    return (
        <Box style={{ maxWidth: 900, paddingBottom: theme.spacing(6) }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb">
                <Link
                    color="inherit"
                    to="/settings"
                    style={{
                        color: '#8A8A8A',
                        fontSize: '15px',
                        textDecoration: 'none',
                    }}
                >
                    Settings
                </Link>
                <Typography
                    color="textPrimary"
                    style={{
                        color: '#8A8A8A',
                        fontSize: '15px',
                        textDecoration: 'none',
                    }}
                >
                    General Settings
                </Typography>
            </Breadcrumbs>

            <Typography
                variant="h4"
                style={{
                    fontWeight: 700,
                    color: '#2F2E41',
                    marginBottom: theme.spacing(3),
                    marginTop: theme.spacing(0.5),
                }}
            >
                General Settings
            </Typography>

            {/* ── Workspace Settings Card ── */}
            <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>
                    Workspace Settings
                </Typography>

                {/* Logo Row */}
                <Box className={classes.sectionDivider}>
                    <Typography
                        variant="body2"
                        style={{ fontWeight: 600, marginBottom: 8 }}
                    >
                        Property Logo
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                        <Box className={classes.logoPreviewBox}>
                            {isLogoUploading || isLogoDeleting ? (
                                <CircularProgress size={24} />
                            ) : logoPreview ? (
                                <img
                                    src={logoPreview}
                                    alt="Property logo"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{ textAlign: 'center' }}
                                >
                                    No logo
                                </Typography>
                            )}
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            style={{ gap: 8 }}
                        >
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleLogoUpload}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloudUploadIcon />}
                                onClick={() => logoInputRef.current?.click()}
                                disabled={isLogoUploading || isLogoDeleting}
                                style={{ textTransform: 'none' }}
                            >
                                {logoPreview ? 'Change Logo' : 'Upload Logo'}
                            </Button>
                            {logoPreview && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleLogoRemove}
                                    disabled={isLogoUploading || isLogoDeleting}
                                    style={{
                                        color: '#ef4444',
                                        borderColor: '#fca5a5',
                                        textTransform: 'none',
                                    }}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Address + Map Row */}
                <Box display="flex" style={{ gap: theme.spacing(4) }}>
                    {/* Address Fields */}
                    <Box style={{ flex: 1, maxWidth: 340 }}>
                        <Typography
                            variant="body2"
                            style={{ fontWeight: 600, marginBottom: 12 }}
                        >
                            Property Address
                        </Typography>
                        <AddressForm
                            addressState={addressState}
                            setAddressState={setAddressState}
                            inputStyle={{
                                marginBottom: theme.spacing(1.5),
                            }}
                        />
                        <Box style={{ marginTop: theme.spacing(1) }}>
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Timezone Offset
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {offsetStr}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Map */}
                    <Box
                        style={{
                            width: 320,
                            height: 260,
                            flexShrink: 0,
                            borderRadius: 8,
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                            alignSelf: 'flex-start',
                            marginTop: 28,
                        }}
                    >
                        <iframe
                            width="100%"
                            height="100%"
                            loading="lazy"
                            style={{ border: 0, display: 'block' }}
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${addressState.city}_${addressState.state}`}
                        />
                    </Box>
                </Box>

                {/* Save */}
                <Box style={{ marginTop: theme.spacing(2.5) }}>
                    <Button
                        variant="contained"
                        className={classes.primaryBtn}
                        disabled={addressLoading}
                        onClick={() => {
                            setAddressLoading(true)
                            axiosInstance
                                .post(`/company/update-company-address/`, {
                                    address: addressState,
                                })
                                .then((res) => {
                                    toast.success(`Address updated!`)
                                    if (res.data && res.data.primary_address) {
                                        const pimaryAddress: Address | null =
                                            res.data.primary_address
                                        if (pimaryAddress) {
                                            setAddressState({
                                                addressLine1:
                                                    pimaryAddress.address1,
                                                addressLine2:
                                                    pimaryAddress.address2,
                                                city: pimaryAddress.city,
                                                state: pimaryAddress.state,
                                                zip: pimaryAddress.zip_code,
                                                country: pimaryAddress.country,
                                            })
                                        }
                                        setCompanyTzOffset({
                                            utcOffset:
                                                res.data.primary_address
                                                    .utc_offset,
                                            dstOffset:
                                                res.data.primary_address
                                                    .dst_offset,
                                        })
                                    }
                                })
                                .catch(() => {
                                    toast.error(`Address update failed!`)
                                })
                                .finally(() => {
                                    setAddressLoading(false)
                                })
                        }}
                    >
                        {addressLoading ? (
                            <LoopIcon className={classes.rotateIcon} />
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Box>
            </Box>

            {/* ── User Settings Card ── */}
            <Box className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>
                    User Settings
                </Typography>

                <form
                    noValidate
                    autoComplete="off"
                    className={classes.getQuoteForm}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    {/* Avatar + Name */}
                    <Box
                        display="flex"
                        alignItems="center"
                        className={classes.sectionDivider}
                    >
                        <div
                            style={{
                                width: 80,
                                height: 80,
                                display: 'inline-flex',
                                position: 'relative',
                                flexShrink: 0,
                                borderRadius: '50%',
                                overflow: 'visible',
                            }}
                        >
                            <img
                                src={filePreview}
                                alt=""
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                }}
                            />
                            <input
                                accept="image/png, image/jpeg, image/jpg"
                                className={classes.uploadInput}
                                id="icon-button-file"
                                type="file"
                                onChange={(event) => {
                                    if (event.target.files) {
                                        setProfilePic(event.target.files[0])
                                    }
                                }}
                            />
                            <label htmlFor="icon-button-file">
                                <IconButton
                                    color="primary"
                                    aria-label="upload picture"
                                    component="span"
                                    className={classes.fileUploadBtn}
                                    style={{
                                        color: '#FFF',
                                        background: '#2C80FF',
                                        padding: '4px',
                                    }}
                                >
                                    <PhotoCamera />
                                </IconButton>
                            </label>
                        </div>
                        <Box style={{ paddingLeft: 16 }}>
                            <Typography
                                style={{
                                    fontWeight: 600,
                                    fontSize: '1.15rem',
                                }}
                            >
                                {workspaceUser?.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {workspaceUser?.email}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Notification Preferences */}
                    <Box className={classes.sectionDivider}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={portfolioScheduleReportEnabled}
                                    onChange={
                                        handlePortfolioScheduleReportToggle
                                    }
                                    color="primary"
                                    disabled={isUpdatingPortfolioReport}
                                />
                            }
                            label="Daily Portfolio Schedule Reports"
                        />
                        <Typography variant="body2" color="textSecondary">
                            Receive one daily email with all your active
                            projects rolled into a single report. When this is
                            on, you will not receive the normal single-project
                            daily reports.
                        </Typography>
                    </Box>

                    {/* Form Fields */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormLabel component="legend">Name:</FormLabel>
                            <FormControl fullWidth>
                                <TextField
                                    id="name"
                                    placeholder="Name"
                                    variant="outlined"
                                    size="small"
                                    {...register('name', {
                                        required: true,
                                    })}
                                />
                                {errors.name &&
                                    errors.name.type === 'required' && (
                                        <span className="error-message">
                                            This is required field
                                        </span>
                                    )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel component="legend">Email:</FormLabel>
                            <FormControl fullWidth>
                                <TextField
                                    id="email"
                                    placeholder="Email"
                                    variant="outlined"
                                    size="small"
                                    {...register('email', {
                                        required: true,
                                    })}
                                    disabled
                                />
                                {errors.email &&
                                    errors.email.type === 'required' && (
                                        <span className="error-message">
                                            This is required field
                                        </span>
                                    )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel component="legend">
                                Phone Number:
                            </FormLabel>
                            <FormControl fullWidth>
                                <TextField
                                    id="phone"
                                    placeholder="Phone Number"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.phone}
                                    {...register('phone', {
                                        required: true,
                                    })}
                                />
                                {errors.phone && (
                                    <span className="error-message">
                                        {typeof errors.phone.message ===
                                        'string'
                                            ? errors.phone.message
                                            : 'This is required field'}
                                    </span>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormLabel component="legend">
                                Cell Phone:
                            </FormLabel>
                            <FormControl fullWidth>
                                <TextField
                                    id="cell_phone"
                                    placeholder="Cell Phone"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.cell_phone}
                                    {...register('cell_phone', {
                                        required: true,
                                    })}
                                />
                                {errors.cell_phone && (
                                    <span className="error-message">
                                        {typeof errors.cell_phone.message ===
                                        'string'
                                            ? errors.cell_phone.message
                                            : 'This is required field'}
                                    </span>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Save / Close */}
                    <Box
                        display="flex"
                        style={{ gap: 8, marginTop: theme.spacing(2.5) }}
                    >
                        <Button
                            variant="contained"
                            className={classes.primaryBtn}
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <LoopIcon className={classes.rotateIcon} />
                            ) : (
                                'Save'
                            )}
                        </Button>
                        <Button className={classes.closeBtn}>Close</Button>
                    </Box>
                </form>
            </Box>
        </Box>
    )
}
